import { regex } from "../config/regex";
import Vue from "vue";
import moment from "moment";
import { i18n } from "../main";
import { polishPlurals } from "polish-plurals";

export function textTruncate(str, length, ending) {
  if (length == null) {
    length = 100;
  }
  if (ending == null) {
    ending = "...";
  }
  if (str.length > length) {
    return str.substring(0, length - ending.length) + ending;
  } else {
    return str;
  }
}

export function getCurrentAppLayout(router) {
  let location = router.history.current.fullPath;
  let path = location.split("/");
  return path[1];
}

export function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export function getSocialMediaProfileNameFromUrl(value, regex) {
  const profileName = value.match(regex);

  if (profileName) return profileName[1];
  return value;
}

export function trimUrlProtocol(value) {
  const val = value.replace(regex.urlProtocols, "");

  return val.replace(regex.removeAfterFirstSlash, "");
}

export function getKeyByValue(object, value) {
  return Object.keys(object).find(key => object[key] === value);
}

export function hexToRgbA(hex, alpha) {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = "0x" + c.join("");
    return (
      "rgba(" +
      [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") +
      "," +
      alpha +
      ")"
    );
  }
  throw new Error("Bad Hex");
}

export function getRangeOfDates(
  start,
  end,
  key,
  dateFormat,
  arr = [start.startOf(key).format(dateFormat)]
) {
  if (start.isAfter(end)) throw new Error("start must precede end");
  const next = moment(start)
    .add(1, key)
    .startOf(key);
  if (next.isAfter(end, key)) return arr;
  return getRangeOfDates(
    next,
    end,
    key,
    dateFormat,
    arr.concat(next.format(dateFormat))
  );
}

export function averageLabel(average) {
  if (average >= 0 && average < 20) return i18n.t("message.average.veryWeak");
  if (average >= 20 && average < 41) return i18n.t("message.average.weak");
  if (average >= 41 && average < 75) return i18n.t("message.average.medium");
  if (average >= 75 && average < 90) return i18n.t("message.average.good");
  if (average >= 90 && average <= 100)
    return i18n.t("message.average.veryGood");
}

export const capitalize = value => {
  if (!value) return "";
  value = value.toString();
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/*
 * Generate array of hours 00:00, 00:30, 01:00, 01:30 ...
 * */
export const generateHours = () => {
  const hours = [];

  for (let hour = 0; hour < 24; hour++) {
    if (hour > 0) {
      hours.push(moment({ hour }).format("HH:mm"));
    }
    hours.push(
      moment({
        hour,
        minute: 30
      }).format("HH:mm")
    );
  }

  return hours;
};

export const comparer = otherArray => {
  return function(current) {
    return (
      otherArray.filter(function(other) {
        return other.id === current.id;
      }).length === 0
    );
  };
};

export const sortDateRangeAsc = dates => {
  if (dates.length > 1) {
    return moment(dates[0]).isBefore(dates[1]) ? dates : dates.reverse();
  } else {
    return dates;
  }
};

export const pluralCount = (pluralFormKeys, count) => {
  const keys =
    typeof pluralFormKeys === "string"
      ? new Array(3)
          .fill(pluralFormKeys)
          .map((item, index) => `${item}[${index}]`)
      : pluralFormKeys;
  const translations = keys.map(key => i18n.t(key));
  const plural = polishPlurals(...translations, count);
  return `${count} ${plural}`;
};

export const getRandomBetween = (min, max) => {
  return Math.floor(Math.random() * (max - min) + min);
};

export const scrollToTop = el => {
  el.scrollTo({ top: 0, behavior: "smooth" });
};

export const dev__warnNotify = (text, group = "gtm") => {
  if (process.env.NODE_ENV !== "production") {
    Vue.notify({
      type: "warn",
      group,
      text
    });
  }
};
export function getHeatmapWithLabelColors(data) {
  return data
    .map(item => {
      return {
        ...item,
        label: {
          color: "#181E38"
        }
      };
    })
    .slice(0, 9);
}

const getSentimentTranslations = () => {
  return {
    positive: i18n.t("message.sentiment.positive"),
    negative: i18n.t("message.sentiment.negative"),
    neutral: i18n.t("message.sentiment.neutral"),
    unassigned: i18n.t("message.sentiment.unassigned")
  };
};

const getSentimentRanges = () => {
  return {
    positive: [21, 100],
    negative: [-100, -21],
    neutral: [-20, 20]
  };
};

export const getMostlySentiment = sentiment => {
  const sentimentRanges = getSentimentRanges();
  const sentimentTranslations = getSentimentTranslations();

  const positiveNegativeDifference =
    sentiment.positive.percentage - sentiment.negative.percentage;

  const mostlySentiment = Object.keys(sentimentRanges).find(
    key =>
      positiveNegativeDifference >= sentimentRanges[key][0] &&
      positiveNegativeDifference <= sentimentRanges[key][1]
  );

  return sentimentTranslations[mostlySentiment];
};

export const getSentimentBetweenRange = value => {
  const sentimentRanges = getSentimentRanges();
  const sentimentTranslations = {
    positive: i18n.t("message.sentiment.positive"),
    negative: i18n.t("message.sentiment.negative"),
    neutral: i18n.t("message.sentiment.mixed"),
    unassigned: i18n.t("message.sentiment.unassigned")
  };

  const mostlySentiment = Object.keys(sentimentRanges).find(
    key => value >= sentimentRanges[key][0] && value <= sentimentRanges[key][1]
  );

  return sentimentTranslations[mostlySentiment];
};

export const getCookieValue = name => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2)
    return parts
      .pop()
      .split(";")
      .shift();
};
